import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs } from 'antd';
import { ConfigNav, Container } from './styled';
import SmartAssessmentPageHeader from './SmartAssessmentPageHeader';
import GeneralInfoTabCardViews from './GeneralInfoTabCardViews';
import TransactionInformatoinTab from './TransactionInformatoinTab';
import ScoreExplainability from '../../tmCommon/ScoreExplainability';
import { hasAttributeValue, hasResourceOperation } from '../../../util';
import { isResourceAvailable } from '../../../util';
import AlertHistoryPrevention from '../../tmCommon/components/AlertHistory/AlertHistoryPrevention';
import {
  clearAMLSCommonUnifiedAlerts,
  fetchAMLSAlertLevel,
  getAMLSCommonAlertPartyIdentity
} from '../../../redux/actions/common';
import { connect } from 'react-redux';
import { getSelectedResources } from '../../../util/resourceManager';
import ExportHistory from '../../common/TransactionDetalis/ExportHistory';
import {
  TRANSACTION_HISTORY_MODULES as MODULES,
  resolveDownloadModule,
  resolveActivityDownloadModule
} from '../../constants/commonConstants';
import ActivityInformationTab from './ActivityInformationTab';

const { TabPane } = Tabs;

const { trmResources } = getSelectedResources();

const SmartAssessment = ({
  match,
  location,
  alertId: cmAlertId,
  authentication,
  clearUnifiedAlertsData,
  apiActions,
  alertPartyIdentity,
  alertMetaSuffix,
  alertLevels,
  getAMLSAlertLevel,
  module
}) => {
  const locationSearch = location?.search || window.location.search;
  const urlParams = new URLSearchParams(locationSearch);
  const tabParam = urlParams.get('tab');
  const validTabs = ['1', '2', '3', '4', 'downloadHistory'];
  const defaultTab = validTabs.includes(tabParam) ? tabParam : '1';
  const [tab, setTab] = useState(defaultTab);
  // FIN-11649 (B4 FE-integration): refreshKey lifted here so that an Activity
  // download bumps the same key that ExportHistory listens on. Bumped by the
  // ActivityInformationTab callback below.
  const [refreshKey, setRefreshKey] = useState(0);
  const onActivityDownloadComplete = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const currentTab = urlParams.get('tab');
    if (currentTab && validTabs.includes(currentTab)) {
      setTab(currentTab);
    }
  }, [locationSearch]);

  const alertId = match?.params?.alertId || cmAlertId;
  const isFromCM = !!cmAlertId;
  const effectiveModule = module || resolveDownloadModule({ isFromCM });
  const isPrevention =
    isFromCM ||
    hasAttributeValue(
      authentication,
      trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
      '24'
    );

  let VIEW_ACCESS_TABS =
    isFromCM ||
    isResourceAvailable(
      authentication,
      trmResources.APP_3_RESOURCE_UI_AMLS_TM_ALERT_DETAILS
    );

  useEffect(() => {
    if (alertId) {
      clearUnifiedAlertsData();

      if (isPrevention) {
        alertId && apiActions({ alertId, isList: isPrevention });
      } else {
        alertId && apiActions({ alertId });
      }

      // Get alert level configs to display alert level label and color based on the config
      if (!alertLevels?.length) {
        getAMLSAlertLevel();
      }
    }
  }, [alertId]);

  // FIN-12062 — generalised tab navigator. The graph panel still calls it with
  // no argument (→ Activity Information, key '3', unchanged); the General-Info
  // party cards pass the context tab derived from the alert's type_of_alert.
  const handleGraphClick = (tabKey = '3') => {
    setTab(tabKey);
  };

  return (
    <Container>
      <SmartAssessmentPageHeader
        isPrevention={isPrevention}
        alertId={alertId}
        isFromCM={isFromCM}
        alertMetaSuffix={alertMetaSuffix}
      />
      <ConfigNav>
        <Tabs
          activeKey={tab}
          onChange={(activeKey) => {
            setTab(activeKey);
          }}>
          {VIEW_ACCESS_TABS && (
            <>
              <TabPane tab="General Information" key="1">
                <GeneralInfoTabCardViews
                  isPrevention={isPrevention}
                  alertId={alertId}
                  alertPartyIdentity={alertPartyIdentity}
                  handleGraphClick={handleGraphClick}
                />
              </TabPane>
              <TabPane tab="Transaction Information" key="2">
                <TransactionInformatoinTab
                  isPrevention={isPrevention}
                  alertId={alertId}
                  isSmartAssessment={isPrevention}
                  module={effectiveModule}
                />
              </TabPane>
              {isPrevention && (
                <TabPane tab="Activity Information" key="3">
                  <ActivityInformationTab
                    isPrevention={isPrevention}
                    alertId={alertId}
                    isSmartAssessment={isPrevention}
                    onActivityDownloadComplete={onActivityDownloadComplete}
                    module={resolveActivityDownloadModule(effectiveModule)}
                  />
                </TabPane>
              )}
              <TabPane
                tab={isPrevention ? 'Explanation' : 'Score explanation'}
                key="4">
                <ScoreExplainability
                  isPrevention={isPrevention}
                  alertId={alertId}
                />
              </TabPane>
              {isPrevention && (
                <TabPane tab="Alert History" key="5">
                  <AlertHistoryPrevention
                    alertPartyIdentity={alertPartyIdentity}
                    isFromCM={isFromCM}
                  />
                </TabPane>
              )}
              <TabPane tab="Download History" key="downloadHistory">
                <ExportHistory
                  alertId={alertId}
                  isActive={tab === 'downloadHistory'}
                  module={effectiveModule}
                  refreshKey={refreshKey}
                />
              </TabPane>
            </>
          )}
        </Tabs>
      </ConfigNav>
    </Container>
  );
};

SmartAssessment.propTypes = {
  match: PropTypes.object,
  alertId: PropTypes.string,
  alertMetaSuffix: PropTypes.element,
  alertLevels: PropTypes.array,
  getAMLSAlertLevel: PropTypes.func,
  isFromCM: PropTypes.bool,
  authentication: PropTypes.object,
  clearUnifiedAlertsData: PropTypes.func,
  apiActions: PropTypes.func,
  alertPartyIdentity: PropTypes.object
};

const mapStateToProps = (state) => {
  const { alertPartyIdentity = {}, alertLevelConfigs } = state.amlsCommon;
  return {
    alertPartyIdentity,
    authentication: state.authentication,
    alertLevels: alertLevelConfigs?.data
  };
};

const mapDispatchToProps = (dispatch) => ({
  apiActions: (alertId) => dispatch(getAMLSCommonAlertPartyIdentity(alertId)),
  clearUnifiedAlertsData: () => dispatch(clearAMLSCommonUnifiedAlerts()),
  getAMLSAlertLevel: () => dispatch(fetchAMLSAlertLevel())
});

export default connect(mapStateToProps, mapDispatchToProps)(SmartAssessment);
